import { MAZE_SIZE, PLAYER_SIZE } from "./mazeData";

export function toRect(left, top, width, height) {
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
  };
}

export function playerRect(pos, size = PLAYER_SIZE) {
  return toRect(pos.left, pos.top, size, size);
}

export function overlaps(a, b) {
  if (a.left >= b.right || b.left >= a.right) return false;
  if (a.top >= b.bottom || b.top >= a.bottom) return false;
  return true;
}

export function touches(a, b) {
  if (a.left > b.right || b.left > a.right) return false;
  if (a.top > b.bottom || b.top > a.bottom) return false;
  return true;
}

export function insideGameArea(rect, size = MAZE_SIZE) {
  return !(
    rect.left < 0 ||
    rect.top < 0 ||
    rect.right > size ||
    rect.bottom > size
  );
}

export function isValidPlayerPosition(rect, walls) {
  if (!insideGameArea(rect)) return false;
  for (let i = 0; i < walls.length; i += 1) {
    if (overlaps(rect, walls[i])) return false;
  }
  return true;
}

function sign(num) {
  return num > 0 ? 1 : num < 0 ? -1 : 0;
}

/**
 * Walk 1px along a movement vector and keep the farthest valid step so the
 * player slides up to walls instead of stopping a full step early.
 */
function walkAxis(pos, deltaX, deltaY, walls, playerSize) {
  if (!deltaX && !deltaY) return null;

  let xIncrement;
  let yIncrement;
  let numIterations;
  if (Math.abs(deltaX) >= Math.abs(deltaY)) {
    xIncrement = sign(deltaX);
    yIncrement = deltaX ? (xIncrement / deltaX) * deltaY : 0;
    numIterations = Math.max(1, Math.ceil(Math.abs(deltaX)));
  } else {
    yIncrement = sign(deltaY);
    xIncrement = deltaY ? (yIncrement / deltaY) * deltaX : 0;
    numIterations = Math.max(1, Math.ceil(Math.abs(deltaY)));
  }

  let validStep = { left: pos.left, top: pos.top };
  let stepped = false;
  for (let i = 0; i < numIterations; i += 1) {
    const nextStep = {
      left: validStep.left + xIncrement,
      top: validStep.top + yIncrement,
    };
    if (isValidPlayerPosition(playerRect(nextStep, playerSize), walls)) {
      validStep = nextStep;
      stepped = true;
      continue;
    }

    // Snap to integer coords once — helps when a fractional step clips a wall.
    const floored = {
      left: Math.floor(nextStep.left),
      top: Math.floor(nextStep.top),
    };
    if (
      (floored.left !== nextStep.left || floored.top !== nextStep.top) &&
      isValidPlayerPosition(playerRect(floored, playerSize), walls)
    ) {
      validStep = floored;
      stepped = true;
    }
    break;
  }

  return stepped ? validStep : null;
}

/**
 * Move with wall-sliding: try the full vector, then X-only, then Y-only.
 * Diagonal input no longer wedges the player against corners.
 */
export function getNewPosition(pos, deltaX, deltaY, walls, playerSize = PLAYER_SIZE) {
  if (!deltaX && !deltaY) return null;

  const full = walkAxis(pos, deltaX, deltaY, walls, playerSize);
  if (full && (full.left !== pos.left || full.top !== pos.top)) {
    // If both axes moved (or it was a single-axis request), keep it.
    const movedX = full.left !== pos.left;
    const movedY = full.top !== pos.top;
    if ((deltaX && deltaY && movedX && movedY) || !(deltaX && deltaY)) {
      return full;
    }
    // Diagonal got stuck on one axis — fall through to slide.
  }

  let next = { left: pos.left, top: pos.top };
  if (deltaX) {
    const xOnly = walkAxis(next, deltaX, 0, walls, playerSize);
    if (xOnly) next = xOnly;
  }
  if (deltaY) {
    const yOnly = walkAxis(next, 0, deltaY, walls, playerSize);
    if (yOnly) next = yOnly;
  }

  if (next.left === pos.left && next.top === pos.top) return null;
  return next;
}

export function itemRect(item, mazeSize = MAZE_SIZE) {
  return toRect(
    (item.leftPct / 100) * mazeSize,
    (item.topPct / 100) * mazeSize,
    item.width,
    item.height
  );
}
